import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Shield,
  CircleNotch,
  Eye,
  PencilSimple,
  Plus,
  Trash,
  FloppyDisk,
  ArrowCounterClockwise,
  Lock,
  Fingerprint,
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  super_admin: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  admin: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  manager: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  staff: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  auditor: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
};

const permissionIcons = { view: Eye, create: Plus, edit: PencilSimple, delete: Trash, mark: Fingerprint };
const permissionLabels = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', mark: 'Clock-in/out' };
const moduleLabels = {
  hr: 'HR', accounts: 'Accounts', sales: 'Sales',
  users: 'Users', roles: 'Roles', settings: 'Settings',
  attendance: 'Attendance',
};

export default function RolesPage() {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);       // response of /rbac/effective-matrix
  const [draft, setDraft] = useState({});       // { role: { module: [perms...] } } — user's unsaved edits
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);

  const canEdit = ['admin', 'super_admin'].includes(currentUser?.role);

  useEffect(() => { fetchMatrix(); }, []);

  const fetchMatrix = async () => {
    try {
      const res = await axios.get(`${API_URL}/rbac/effective-matrix`);
      setData(res.data);
      // Seed draft from matrix so toggles work locally before save
      const d = {};
      Object.entries(res.data.matrix || {}).forEach(([role, mods]) => {
        d[role] = {};
        Object.entries(mods).forEach(([mod, perms]) => { d[role][mod] = [...(perms || [])]; });
      });
      setDraft(d);
    } catch {
      toast.error('Failed to load permission matrix');
    } finally { setLoading(false); }
  };

  const isEditableRole = (role) => (data?.overridable_roles || []).includes(role);

  const togglePerm = (role, mod, perm) => {
    if (!canEdit || !isEditableRole(role)) return;
    setDraft(prev => {
      const roleMap = { ...(prev[role] || {}) };
      const list = new Set(roleMap[mod] || []);
      if (list.has(perm)) list.delete(perm); else list.add(perm);
      roleMap[mod] = Array.from(list);
      return { ...prev, [role]: roleMap };
    });
  };

  const roleChanged = (role) => {
    const orig = data?.matrix?.[role] || {};
    const cur = draft?.[role] || {};
    const mods = new Set([...Object.keys(orig), ...Object.keys(cur)]);
    for (const m of mods) {
      const a = [...(orig[m] || [])].sort().join(',');
      const b = [...(cur[m] || [])].sort().join(',');
      if (a !== b) return true;
    }
    return false;
  };

  const saveRole = async (role) => {
    setSavingRole(role);
    try {
      await axios.put(`${API_URL}/rbac/permissions/${role}`, { permissions: draft[role] || {} });
      toast.success(`${roleLabel(role)} permissions saved`);
      await fetchMatrix();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save permissions');
    } finally { setSavingRole(null); }
  };

  const resetRole = async (role) => {
    if (!window.confirm(`Reset ${roleLabel(role)} to system defaults?`)) return;
    setSavingRole(role);
    try {
      await axios.post(`${API_URL}/rbac/permissions/${role}/reset`);
      toast.success(`${roleLabel(role)} reset to defaults`);
      await fetchMatrix();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reset');
    } finally { setSavingRole(null); }
  };

  const roleLabel = (r) => data?.role_info?.[r]?.name || r;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const roles = Object.keys(data?.matrix || {});
  const modules = data?.modules || [];
  const perms = data?.permissions || [];

  return (
    <div className="space-y-8" data-testid="roles-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
          Roles &amp; Permissions
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
          {canEdit
            ? 'Toggle any cell to grant / revoke a permission. Click "Save" on a role row to apply. Super Admin is not editable.'
            : 'View-only. Ask your Company Admin to change any of these permissions.'}
        </p>
      </motion.div>

      {/* Permission Matrix Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-tactile rounded-lg overflow-hidden"
        data-testid="permission-matrix"
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-medium">Permission Matrix</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Click a permission icon to toggle it. Green = granted. Grey = denied.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-4 text-xs uppercase font-bold text-muted-foreground sticky left-0 bg-muted/50">
                  Role
                </th>
                {modules.map(mod => (
                  <th key={mod} className="text-center px-4 py-4 text-xs uppercase font-bold text-muted-foreground min-w-[140px]">
                    {moduleLabels[mod] || mod}
                  </th>
                ))}
                {canEdit && (
                  <th className="text-right px-4 py-4 text-xs uppercase font-bold text-muted-foreground min-w-[160px]">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {roles.map(role => {
                const editable = isEditableRole(role);
                const changed = roleChanged(role);
                return (
                  <tr key={role} className="hover:bg-muted/20" data-testid={`role-row-${role}`}>
                    <td className="px-6 py-4 sticky left-0 bg-background">
                      <Badge variant="outline" className={roleColors[role]}>{roleLabel(role)}</Badge>
                      {!editable && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                          <Lock size={10} /> Fixed by platform
                        </div>
                      )}
                    </td>
                    {modules.map(mod => (
                      <td key={`${role}-${mod}`} className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1.5">
                          {perms.map(perm => {
                            const granted = (draft?.[role]?.[mod] || []).includes(perm);
                            const Icon = permissionIcons[perm];
                            const clickable = canEdit && editable;
                            return (
                              <button
                                key={perm}
                                type="button"
                                disabled={!clickable}
                                onClick={() => togglePerm(role, mod, perm)}
                                title={`${permissionLabels[perm]}: ${granted ? 'granted' : 'denied'}${clickable ? ' — click to toggle' : ''}`}
                                data-testid={`perm-${role}-${mod}-${perm}`}
                                className={`w-8 h-8 rounded flex items-center justify-center transition ${
                                  granted
                                    ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                                    : 'bg-zinc-500/10 text-zinc-500 hover:bg-zinc-500/20'
                                } ${!clickable ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {Icon && <Icon size={14} weight={granted ? 'fill' : 'regular'} />}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    ))}
                    {canEdit && (
                      <td className="px-4 py-4 text-right">
                        {editable ? (
                          <div className="flex items-center gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resetRole(role)}
                              disabled={savingRole === role}
                              data-testid={`reset-${role}`}
                            >
                              <ArrowCounterClockwise size={14} /> Reset
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => saveRole(role)}
                              disabled={!changed || savingRole === role}
                              data-testid={`save-${role}`}
                            >
                              {savingRole === role ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />}
                              {changed ? ' Save' : ' Saved'}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-xs uppercase font-bold text-muted-foreground">Legend:</span>
            {Object.entries(permissionIcons).map(([key, Icon]) => (
              <div key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-6 h-6 rounded bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
                  <Icon size={13} weight="fill" />
                </div>
                {permissionLabels[key]}
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Current user summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading font-medium text-lg mb-4">Your Current Role</h3>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${roleColors[currentUser?.role]?.split(' ')[0] || 'bg-primary/10'}`}>
            <Shield size={28} weight="duotone" className={roleColors[currentUser?.role]?.split(' ').slice(1).join(' ') || 'text-primary'} />
          </div>
          <div>
            <Badge variant="outline" className={roleColors[currentUser?.role]}>
              {currentUser?.role?.replace('_', ' ').toUpperCase()}
            </Badge>
            <p className="text-sm text-muted-foreground mt-1">
              {data?.role_info?.[currentUser?.role]?.description}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
